/**
 * ProfileAchievements.jsx — Feb 28 2026 user request:
 *   "Wire achievements to read listening minutes of audio and cinema
 *    and award coins" + reader-only achievements page.
 *
 * Surfaces:
 *   • Listening journey (audio + cinema, pooled by listening_stats)
 *   • Writer journey milestones (for writers/admins) — coins earned per
 *     published-word threshold
 *   • Reading speed / reading minutes panel (existing reader gamification)
 *
 * Strictly persona-aware: readers see ONLY the listening + reading-speed
 * cards. Writers + admins see those PLUS the scribe journey card.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, Headphones, BookOpen, Crown, Coins, Sparkles,
  Zap, ArrowLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { checkAuth, authFetch } from '../utils/authUtils';
import { effectiveRoleFor, getActiveSpace, getAdminViewingAs, onSpaceChange } from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
  catch { return iso; }
};
const fmtMins = (n) => {
  const m = Number(n || 0);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};

const TileGrid = ({ items, emptyMsg, testidPrefix, unit }) => {
  if (!items || items.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid={`${testidPrefix}-empty`}>{emptyMsg}</p>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testidPrefix}-grid`}>
      {items.map((a) => (
        <div
          key={`${a.milestone_key}:${a.series_id || a.kind || ''}`}
          className="rounded-xl bg-slate-800/60 border border-amber-400/30 p-3"
          data-testid={`${testidPrefix}-tile-${a.milestone_key}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-2xl">🪙</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-300">{a.milestone_key}</span>
          </div>
          <p className="text-white font-bold text-sm mt-2 tabular-nums">+{a.coins_awarded} coins</p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {fmtNum(a.threshold_minutes || a.threshold_words)} {unit}
            <span className="text-slate-500"> · {fmtDate(a.awarded_at)}</span>
          </p>
        </div>
      ))}
    </div>
  );
};

const ProfileAchievements = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [reader, setReader] = useState(null); // listening journey snapshot
  const [writer, setWriter] = useState(null); // writer journey snapshot
  const [listenStats, setListenStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Subscribe to admin-viewing-as space changes so swapping persona via
  // the bottom nav re-renders this page without a full reload.
  const [, setSpaceTick] = useState(0);
  useEffect(() => onSpaceChange(() => setSpaceTick((n) => n + 1)), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      if (cancelled) return;
      setUser(u);
      const [r1, r2, r3] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/api/reader/journey`).then((r) => r.json()),
        (u.role === 'writer' || u.role === 'admin')
          ? authFetch(`${BACKEND_URL}/api/writer/journey`).then((r) => r.json())
          : Promise.resolve(null),
        authFetch(`${BACKEND_URL}/api/listening/stats`).then((r) => r.json()),
      ]);
      if (cancelled) return;
      if (r1.status === 'fulfilled') setReader(r1.value);
      if (r2.status === 'fulfilled') setWriter(r2.value);
      if (r3.status === 'fulfilled') setListenStats(r3.value);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-gray-400">Loading achievements…</p></div>;
  }

  const isWriterOrAdmin = user && (user.role === 'writer' || user.role === 'admin');
  // Persona resolution — mirrors Profile.jsx. Writers and admins viewing-as
  // writer get a WRITER-ONLY achievements page (no listening / reading tier).
  const realRole = (user?.role || 'user').toLowerCase();
  const effective = effectiveRoleFor(realRole, getActiveSpace());
  const adminViewing = getAdminViewingAs();
  let persona = effective;
  if (realRole === 'admin' && adminViewing === 'admin') persona = 'admin';
  const isWriterPersona = persona === 'writer';
  const isReaderPersona = persona === 'user';
  const writerMilestones = (writer?.achievements || []).map((m) => ({ ...m, _unit: 'words' }));
  const listeningMilestones = reader?.achievements || [];

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="achievements-page">
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate('/profile')} className="p-2 rounded-lg hover:bg-slate-800" data-testid="achievements-back">
          <ArrowLeft className="w-4 h-4 text-slate-300" />
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <Trophy className="w-7 h-7 text-amber-300" /> Achievements
        </h1>
      </div>

      {/* Headline pills — quick totals */}
      <section className="grid grid-cols-3 gap-2 sm:gap-3 mb-6" data-testid="achievements-headline">
        <div className="rounded-2xl bg-amber-900/30 border border-amber-500/30 p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-200/80">Coins Earned</p>
          <p className="text-xl sm:text-2xl font-black text-amber-200 tabular-nums" data-testid="achievements-total-coins">
            +{fmtNum((isWriterPersona ? 0 : (reader?.total_coins_from_listening || 0)) + (writer?.total_coins_from_journey || 0))}
          </p>
        </div>
        {isWriterPersona ? (
          <div className="rounded-2xl bg-purple-900/30 border border-purple-500/30 p-3 text-center">
            <p className="text-[10px] uppercase tracking-widest font-bold text-purple-200/80">Effective</p>
            <p className="text-xl sm:text-2xl font-black text-purple-200 tabular-nums" data-testid="achievements-effective-units">
              {fmtNum(writer?.effective_units || 0)}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl bg-cyan-900/30 border border-cyan-500/30 p-3 text-center">
            <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-200/80">Listening</p>
            <p className="text-xl sm:text-2xl font-black text-cyan-200 tabular-nums" data-testid="achievements-listening-mins">
              {fmtMins(reader?.lifetime_minutes || 0)}
            </p>
          </div>
        )}
        <div className="rounded-2xl bg-emerald-900/30 border border-emerald-500/30 p-3 text-center">
          <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-200/80">Milestones</p>
          <p className="text-xl sm:text-2xl font-black text-emerald-200 tabular-nums" data-testid="achievements-total-milestones">
            {(isWriterPersona ? 0 : listeningMilestones.length) + writerMilestones.length}
          </p>
        </div>
      </section>

      {/* Listening milestones — hidden in writer persona per Feb 28 2026
          spec: "remove reading achievements and its everything from writer
          profile". Reader + admin-viewing-as-admin still see it. */}
      {!isWriterPersona && (
        <section className="mb-7" data-testid="achievements-listening">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2">
              <Headphones className="w-4 h-4 text-cyan-300" /> Listening Journey
            </h2>
            {reader?.next_milestone && (
              <span className="text-[11px] text-cyan-200">
                Next: <span className="font-bold">{fmtMins(reader.next_milestone.threshold_minutes)}</span>
                {' '}+{reader.next_milestone.coins_reward} coins
                <span className="text-slate-500"> ({fmtMins(reader.next_milestone.remaining)} to go)</span>
              </span>
            )}
          </div>
          <TileGrid
            items={listeningMilestones}
            emptyMsg="Listen to 60 minutes of any audio or cinema to unlock your first +100 coin milestone."
            testidPrefix="achievements-listening"
            unit="minutes"
          />
        </section>
      )}

      {/* Reading & Listening Tier — also hidden for writer persona. */}
      {!isWriterPersona && listenStats?.progression && (
        <section className="mb-7" data-testid="achievements-reading-speed">
          <h2 className="text-sm uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-fuchsia-300" /> Reading & Listening Tier
          </h2>
          <div className="rounded-2xl bg-gradient-to-br from-fuchsia-900/40 via-slate-800/40 to-cyan-900/30 border border-fuchsia-500/30 p-5">
            <p className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-200/80">Current Evolution</p>
            <h3 className="text-2xl font-black text-white tabular-nums mt-0.5" data-testid="achievements-tier-name">
              {listenStats.progression.evolution_name}
            </h3>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-900/50 p-2">
                <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Level</p>
                <p className="text-base font-black text-white tabular-nums">{listenStats.progression.level_in_evolution}/10</p>
              </div>
              <div className="rounded-lg bg-slate-900/50 p-2">
                <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Total XP</p>
                <p className="text-base font-black text-cyan-200 tabular-nums">{fmtNum(listenStats.lifetime_xp_from_listening)}</p>
              </div>
              <div className="rounded-lg bg-slate-900/50 p-2">
                <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">To Next</p>
                <p className="text-base font-black text-fuchsia-200 tabular-nums">
                  {fmtMins(listenStats.mins_to_next_level_at_2x)}
                  <span className="text-[9px] text-slate-400 font-normal"> at 2x</span>
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Writer journey card */}
      {isWriterOrAdmin && writer && (
        <section className="mb-7" data-testid="achievements-writer">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-300" /> Scribe Journey
            </h2>
            <Button
              onClick={() => navigate('/writer-journey')}
              variant="outline"
              size="sm"
              className="text-xs border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
              data-testid="achievements-writer-cta"
            >
              Full Journey
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mb-3">
            <span className="text-amber-200 font-bold tabular-nums">{fmtNum(writer.lifetime_words || 0)}</span> lifetime words ·
            <span className="text-amber-200 font-bold ml-1">{writerMilestones.length}</span> milestones ·
            <span className="text-amber-200 font-bold ml-1 tabular-nums">+{fmtNum(writer.total_coins_from_journey || 0)} coins</span>
          </p>
          <TileGrid
            items={writerMilestones}
            emptyMsg="Publish 10K words on any series to unlock your first +100 coin milestone."
            testidPrefix="achievements-writer"
            unit="words"
          />
        </section>
      )}

      {/* Footer hint */}
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <Sparkles className="w-3.5 h-3.5" />
        <span>Listening rewards: 60m = +100 · 5h = +300 · Every +10h thereafter = +100</span>
      </div>
    </main>
  );
};

export default ProfileAchievements;
